import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { Seo } from "@/components/Seo";
import { SeoLinks } from "@/components/SeoLinks";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal, MapIcon, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PROPERTY_TYPES, FACING_OPTIONS, APPROVAL_OPTIONS } from "@/lib/format";
import { LANDINGS, TYPE_SEO, DEFAULT_LISTING_SEO } from "@/lib/landing";

export default function Listings({ landing }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [showMore, setShowMore] = useState(false);
  const [minArea, setMinArea] = useState(params.get("min_area") || "");
  const [maxArea, setMaxArea] = useState(params.get("max_area") || "");

  const cfg = landing ? LANDINGS[landing] : null;
  const preset = cfg?.preset || {};

  const urlFilters = {
    q: params.get("q") || undefined,
    listing_type: params.get("listing_type") || undefined,
    property_type: params.get("property_type") || undefined,
    city: params.get("city") || undefined,
    bedrooms: params.get("bedrooms") || undefined,
    facing: params.get("facing") || undefined,
    approval: params.get("approval") || undefined,
    min_area: params.get("min_area") || undefined,
    max_area: params.get("max_area") || undefined,
    verified: params.get("verified") || undefined,
    sort: params.get("sort") || undefined,
  };
  Object.keys(urlFilters).forEach((k) => urlFilters[k] === undefined && delete urlFilters[k]);
  const filters = { sort: "recent", ...preset, ...urlFilters };
  const pageSeo = cfg?.seo || TYPE_SEO[filters.property_type] || DEFAULT_LISTING_SEO;

  const { data = [], isLoading } = useQuery({
    queryKey: ["properties", landing || "", params.toString()],
    queryFn: async () => (await api.get("/properties", { params: filters })).data,
  });

  const update = (key, value) => {
    const p = new URLSearchParams(params);
    if (!value || value === "all") p.delete(key); else p.set(key, value);
    setParams(p);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8">
      <Seo title={pageSeo.title} description={pageSeo.description} path={pageSeo.path} />
      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm sticky top-[72px] z-30">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex items-center gap-2 flex-1 border rounded-xl px-3">
            <Search className="w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && update("q", q)}
              placeholder="Search city, locality, project..." className="border-0 focus-visible:ring-0 px-0" data-testid="listings-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Select value={filters.property_type || "all"} onValueChange={(v) => update("property_type", v)}>
              <SelectTrigger data-testid="filter-property-type"><SelectValue placeholder="Property" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.bedrooms || "all"} onValueChange={(v) => update("bedrooms", v)}>
              <SelectTrigger data-testid="filter-bedrooms"><SelectValue placeholder="BHK" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any BHK</SelectItem>
                {[1, 2, 3, 4].map((b) => <SelectItem key={b} value={String(b)}>{b}+ BHK</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.sort} onValueChange={(v) => update("sort", v)}>
              <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Newest</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => navigate(`/map?${params.toString()}`)} variant="outline" className="rounded-xl" data-testid="listings-map-btn">
            <MapIcon className="w-4 h-4 mr-2" /> Map
          </Button>
          <Button onClick={() => setShowMore((s) => !s)} variant="outline" className="rounded-xl" data-testid="listings-more-filters-btn">
            <SlidersHorizontal className="w-4 h-4 mr-2" /> Land filters
          </Button>
        </div>

        {showMore && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="land-filters-row">
            <Select value={filters.facing || "all"} onValueChange={(v) => update("facing", v)}>
              <SelectTrigger data-testid="filter-facing"><SelectValue placeholder="Facing" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Facing</SelectItem>
                {FACING_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.approval || "all"} onValueChange={(v) => update("approval", v)}>
              <SelectTrigger data-testid="filter-approval"><SelectValue placeholder="Approval" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Approval</SelectItem>
                {APPROVAL_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="filter-min-area" inputMode="numeric" value={minArea} placeholder="Min extent (area)"
              onChange={(e) => setMinArea(e.target.value.replace(/\D/g, ""))}
              onBlur={() => update("min_area", minArea)} onKeyDown={(e) => e.key === "Enter" && update("min_area", minArea)} />
            <Input data-testid="filter-max-area" inputMode="numeric" value={maxArea} placeholder="Max extent (area)"
              onChange={(e) => setMaxArea(e.target.value.replace(/\D/g, ""))}
              onBlur={() => update("max_area", maxArea)} onKeyDown={(e) => e.key === "Enter" && update("max_area", maxArea)} />
            <button type="button" data-testid="filter-verified" onClick={() => update("verified", filters.verified ? "" : "1")}
              className={`h-10 rounded-md border text-sm font-semibold transition-colors ${filters.verified ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
              ✓ Verified only
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 mb-4">
        <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900">{pageSeo.h1}</h1>
        <p className="text-sm text-slate-500 mt-1" data-testid="listings-count">
          {isLoading ? "Loading..." : `${data.length} properties found`}
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-24 text-slate-500">
          <SlidersHorizontal className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          No properties match your filters. Try adjusting them.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      )}

      <SeoLinks />
    </div>
  );
}
